// Runtime support for generated screens. Generated by `sui generate` from .sui sources; do not edit.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { SuiOperations } from "./operations.js";

/** A handler with no arguments. */
export type Action = () => void | Promise<void>;

/** A state variable a widget reads and writes. */
export interface Binding<T> {
  readonly value: T;
  set(value: T): void;
}

/** The result of a `query`. */
export interface Query<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  reload(): void;
}

const OperationsContext = createContext<SuiOperations | null>(null);

export function SuiOperationsProvider({ value, children }: { value: SuiOperations; children?: ReactNode }) {
  return <OperationsContext.Provider value={value}>{children}</OperationsContext.Provider>;
}

export function useOperations(): SuiOperations {
  const operations = useContext(OperationsContext);
  if (!operations) throw new Error("Wrap generated screens in <SuiOperationsProvider>.");
  return operations;
}

const NavigationContext = createContext<((to: string) => void) | null>(null);

export function SuiNavigationProvider({ value, children }: { value: (to: string) => void; children?: ReactNode }) {
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigate(): (to: string) => void {
  const navigate = useContext(NavigationContext);
  if (!navigate) throw new Error("Wrap generated screens in <SuiNavigationProvider>.");
  return navigate;
}

export function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Loads on mount and whenever `deps` change; the latest request wins. */
export function useQuery<T>(fetcher: () => Promise<T>, deps: readonly unknown[]): Query<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  const latest = useRef(0);
  const reload = useCallback(() => {
    latest.current += 1;
    const ticket = latest.current;
    fetcher().then(
      (data) => {
        if (latest.current === ticket) setState({ data, error: null });
      },
      (cause: unknown) => {
        if (latest.current === ticket) setState((previous) => ({ data: previous.data, error: messageOf(cause) }));
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data: state.data, loading: state.data === null && state.error === null, error: state.error, reload };
}
